import { Injectable } from '@angular/core';
import L from 'leaflet';
import { GeolocationService } from '../geolocation/geolocation.service';
import { Subscription } from 'rxjs';
import { Position } from '@capacitor/geolocation';

@Injectable({
  providedIn: 'root'
})
export class OpenmapService {

  private map: L.Map | undefined;
  private markers: L.Marker[] = [];
  private userLocationMarker: L.Marker | undefined;
  private currentLocationSubscription: Subscription | undefined;
  private mapZoomLevel: number = 17;
  userLong : number = 0;
  userLat : number = 0;

  constructor(private _geolocationService: GeolocationService) {
    this.fixLeafletIcons();
  }

  public async initMap(elementId: string, lat: number, lng: number): Promise<L.Map> {

    // Initialize map
    this.map = L.map(elementId).setView([lat, lng], this.mapZoomLevel);
    this.userLocationMarker = this.addMarker(lat, lng, 'You', 'You', undefined, 'user');

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(this.map);

    this.subscribeToLocationChanges();
    
    return this.map;
  }
  
  public addMarker(lat: number, lng: number, popupText?: string, tooltipText?: string, brandImageUrl?: string, markerType: 'user' | 'site' = 'site'): L.Marker {
    if (!this.map) {
      throw new Error('Map not initialized');
    }

    let markerOptions: L.MarkerOptions = {};

    // Create custom icon based on type and image availability
    if (markerType === 'user') {
      markerOptions.icon = this.buildUserLocationIcon();
    } else if (brandImageUrl) {
      markerOptions.icon = this.buildBrandMarkerIcon(brandImageUrl);
    }

    const marker = L.marker([lat, lng], markerOptions).addTo(this.map);
    
    if (popupText) {
      marker.bindPopup(popupText); 
    }

    if (tooltipText) {
      marker.bindTooltip(tooltipText, {
        permanent: true, // Always visible
        direction: 'top',
        offset: L.point(0, -20)
      });
    }

    this.markers.push(marker);
    return marker;

  }

  removeMarker(marker: L.Marker) {
    if (this.map) {
      this.map.removeLayer(marker);
      const index = this.markers.indexOf(marker);
      if (index > -1) {
        this.markers.splice(index, 1);
      }
    }
  }

  clearMarkers() {
    this.markers.forEach(marker => {
      if (this.map) {
        this.map.removeLayer(marker);
      }
    });
    this.markers = [];
  }

  private subscribeToLocationChanges(): void {
    this.currentLocationSubscription = this._geolocationService.currentLocationObservable$.subscribe(
      (position: Position | null) => {
        if (position) {

          if(position.coords.latitude.toFixed(3) != this.userLat.toFixed(3) || 
              position.coords.longitude.toFixed(3) != this.userLong.toFixed(3)) {
            this.map?.setView([position.coords.latitude, position.coords.longitude], this.mapZoomLevel);
          }

          this.userLat = position.coords.latitude;
          this.userLong = position.coords.longitude;
          this.userLocationMarker?.setLatLng([this.userLat, this.userLong]);
        }
      }
    );
  }

  private fixLeafletIcons() {
    delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: string })._getIconUrl;

    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });

  }

  private buildBrandMarkerIcon(brandImageUrl: string): L.Icon {
    return L.icon({
      iconUrl: brandImageUrl,
      iconRetinaUrl: brandImageUrl,
      iconSize: [110, 35], // Square size for brand logos
      iconAnchor: [55, 35], // Bottom center of the icon
      popupAnchor: [0, -35], // Popup appears above the icon
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      shadowSize: [35, 35],
      shadowAnchor: [10, 35],
      className: 'brand-marker-icon' // For custom CSS styling
    });
  }

  private buildUserLocationIcon(): L.Icon {
    return L.icon({
      iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
          <circle cx="12" cy="12" r="8" fill="#4285F4" stroke="#fff" stroke-width="2"/>
          <circle cx="12" cy="12" r="3" fill="#fff"/>
        </svg>
      `),
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16],
      className: 'user-location-icon'
    });
  }

  ngOnDestroy(): void {
    if (this.currentLocationSubscription) {
      this.currentLocationSubscription.unsubscribe();
    }

    if (this.map) {
      try {
        this.map.remove();
      } catch (error) {
        console.warn('Error destroying map:', error);
      }
      this.map = undefined;
    }
  }
}
